import { useAppSelector } from 'store/hooks';
import { QueryStatus } from '@reduxjs/toolkit/query';
import { FIFTEEN_DAYS_MS } from 'shared/constants/time';
import { getTrialDaysLeft } from 'shared/helpers/getTrialDaysLeft';
import { isProjectReportGenerating } from 'shared/helpers/projectStatus';
import {
  Project,
  ReportInfo,
  UserInfoResponse,
  PaidStatus,
  DomainStatus,
} from 'store/api/apiTypes';
import { userApiSlice } from 'store/api/userApiSlice';

const isStandalone = process.env.REACT_APP_TYPE === 'standalone';

const getLocalDayStartMs = (date: Date): number =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

export const useCachedSelectedProject = (): {
  selectedProject: Project | null;
  isLoading: boolean;
  inactiveProjects: string[] | undefined;
  activeProjects: Project[] | undefined;
  reportGenerating: boolean;
  isUnavailable: boolean;
  isTrial: boolean;
  isTrialExpired: boolean;
  daysLeft: number | null; // remaining trial days (0..14) or null if not applicable
  isError?: boolean;
  lastReport?: ReportInfo;
  userInfo?: UserInfoResponse;
  generationAvailableDate: Date;
  allowGeneration: boolean;
  testingB: boolean;
  visionRequested: boolean;
  domainId?: string | undefined;
  showHistory: boolean;
} => {
  const selectedProject = useAppSelector(
    state => state.project.selectedProject
  );
  const reportGenerating = selectedProject
    ? isProjectReportGenerating(selectedProject)
    : false;

  const userInfoState = useAppSelector(
    userApiSlice.endpoints.getUserInfo.select()
  );
  const inactiveProjects = userInfoState.data?.inactiveDomains;
  const activeProjects = userInfoState.data?.activeDomains;
  const abTesting = userInfoState.data?.ab_testing;

  const isTrial = isStandalone
    ? selectedProject?.data?.domain_state === DomainStatus.TRIAL
    : selectedProject?.paid === PaidStatus.TRIAL;
  const isUnavailable =
    selectedProject?.data?.domain_state === DomainStatus.TERMINATED ||
    selectedProject?.paid === PaidStatus.TERMINATED;

  const lastReport = selectedProject?.reports?.[0];
  const daysLeft = isTrial ? getTrialDaysLeft(lastReport) : null;
  const isCanceled = selectedProject?.paid === PaidStatus.EXPIRED;
  const isTrialExpired = isTrial ? daysLeft === 0 : isCanceled ? true : false;
  const isError =
    selectedProject?.reports &&
    selectedProject?.reports?.length > 0 &&
    selectedProject.reports[0].status === 'ERROR';
  const isLoading =
    userInfoState?.status === QueryStatus.pending ||
    userInfoState?.status === QueryStatus.uninitialized;

  const generationAvailableDate: Date = (() => {
    const lastReportMs = new Date(
      lastReport?.date ?? selectedProject?.lastReportDate ?? ''
    ).getTime();
    const fallbackBase = Number.isNaN(lastReportMs) ? Date.now() : lastReportMs;
    const countdownSeconds = selectedProject?.nextCreateCountdown;
    if (typeof countdownSeconds === 'number') {
      if (countdownSeconds < 0) {
        return new Date(Date.now() + -countdownSeconds * 1000);
      }
      if (countdownSeconds === 0) {
        return new Date(fallbackBase + FIFTEEN_DAYS_MS);
      }
      return new Date();
    }
    return new Date(fallbackBase + FIFTEEN_DAYS_MS);
  })();

  const newUser = new Date('2025-12-06') < new Date(lastReport?.date ?? '');
  const testingB = abTesting === 'B' && newUser;
  const visionRequested = !!selectedProject?.data?.vision_mode_access_requested;
  const domainId = selectedProject?.data?.id;
  const allowGeneration =
    selectedProject?.nextCreateCountdown === 0
      ? true
      : getLocalDayStartMs(generationAvailableDate) <=
        getLocalDayStartMs(new Date());

  const reports = selectedProject?.reports;
  const showHistory = !!reports?.some(
    r => r.id !== reports[0].id && r.status !== 'BUSY' && r.status !== 'ERROR'
  );

  return {
    userInfo: userInfoState.data,
    visionRequested,
    selectedProject,
    lastReport,
    isLoading,
    inactiveProjects,
    activeProjects,
    reportGenerating,
    isUnavailable,
    isTrial,
    isTrialExpired,
    isError,
    daysLeft,
    generationAvailableDate,
    allowGeneration,
    testingB,
    domainId,
    showHistory,
  };
};
